import * as Survey from 'survey-react';
import { JWT_COOKIE_KEY } from '../../../../consts';
import { client } from '../../../../apollo';
import {
  GET_CSVFILE_ROWS_SELECTCOLUMNS,
  GET_CSVFILE_ROW_ALLCOLUMNS,
  GET_WORKSPACE_DATASETS,
} from '../../../../graphql';
import { GetCSVFileRowAllColumnsType } from '../../../../graphql/queries/GetCSVFileRowAllColumns';
import { GetCSVFileType, GET_CSVFILE } from '../../../../graphql/queries/GetCSVFile';
import { GetCSVFileRowSelectColumnsType } from '../../../../graphql/queries/GetCSVFileRowsSelectColumns';
import { GetWorkspaceDatasetsType } from '../../../../graphql/queries/GetWorkspaceDatasets';

export type SaveSurveyDataSetType = {
  dataSetId: string;
  itemId: string;
  delete?: boolean;
  updatedAt?: Date;
  values: {
    fieldName: string;
    value: any;
  }[];
};

export const modelHasDataSets = (model: Survey.Model): boolean => {
  const dataSetId: string = model['dataSet'];
  return !!dataSetId;
};

export const getStringOrArrayValue = (value: string): string | string[] => {
  try {
    const answer: string[] = JSON.parse(value.replace(/'/g, '"'));

    if (Array.isArray(answer)) {
      return answer;
    } else if (answer && typeof answer === 'object') {
      return answer;
    } else {
      return value.toString();
    }

    return answer;
  } catch (error) {
    return value.toString();
  }
};

export const configureSurveyForDatasets = async (Survey: any, SurveyCreator?: any, workspaceId?: string) => {
  const isCreator = SurveyCreator !== undefined;
  const Serializer = Survey.Serializer as Survey.JsonMetadata;

  Serializer.addProperty('survey', {
    name: 'dataSet',
    displayName: 'Dataset',
    category: 'Datasets',
    categoryIndex: 50,
    type: 'string',
    ...(isCreator && {
      choices: function(obj: any, choicesCallback: Function) {
        fetchDatasets(workspaceId || '').then(value => {
          const values = [{ value: '', text: 'None' }];
          values.push(...value);
          choicesCallback(values);
        });
      },
    }),
  });

  Survey.Serializer.addProperty('survey', {
    name: 'dataSetActionDeleteElement',
    displayName: 'Dataset Delete Element',
    category: 'Datasets',
    categoryIndex: 50,
    type: 'string',
    ...(isCreator && {
      choices: function(obj: any, choicesCallback: Function) {
        const addElement = (fields: string[], element: any) => {
          if (element.elements) {
            element.elements.forEach((el: any) => {
              addElement(fields, el);
            });
          } else {
            fields.push(element.name);
          }
        };
        const fields: string[] = [];
        obj.pages.forEach((page: any) => {
          page.elements.forEach((el: any) => {
            addElement(fields, el);
          });
        });
        choicesCallback(fields);
      },
    }),
  });

  Survey.Serializer.addProperty('dropdown', {
    name: 'dataSetItemSelector',
    displayName: 'Is Item Selector',
    category: 'Datasets',
    categoryIndex: 50,
    type: 'boolean',
    // ...(isCreator && {
    //   visibleIf: function(obj: any) {
    //     const dataSet = !!obj ? obj.survey['dataSet'] : null;
    //     return dataSet ? true : false;
    //   },
    // }),
  });

  Survey.Serializer.addProperty('dropdown', {
    name: 'dataSetItemCanCreate',
    displayName: 'Can Create New Item',
    category: 'Datasets',
    categoryIndex: 50,
    type: 'boolean',
    // ...(isCreator && {
    //   visibleIf: function(obj: any) {
    //     const dataSet = !!obj ? obj.survey['dataSet'] : null;
    //     return dataSet ? true : false;
    //   },
    // }),
  });

  Survey.Serializer.addProperty('question', {
    name: 'dataSetItemField',
    displayName: 'Field',
    category: 'Datasets',
    categoryIndex: 50,
    type: 'string',
    ...(isCreator && {
      // visibleIf: function(obj: any) {
      //   const dataSet = !!obj ? obj.survey['dataSet'] : null;
      //   return dataSet ? true : false;
      // },
      choices: function(obj: any, choicesCallback: Function) {
        const dataSet = !!obj ? obj.survey['dataSet'] : null;
        if (!dataSet) {
          const fieldName: string = obj['dataSetItemField'] as string;
          if (fieldName) {
            return [
              {
                value: fieldName,
                text: fieldName,
              },
            ];
          }
          return [];
        }
        fetchDatasetFields(dataSet).then(value => {
          choicesCallback(value);
        });
        return [];
      },
    }),
  });

  Survey.ChoicesRestfull.onBeforeSendRequest = onBeforeSendRequest;
};

export const configureSurveyModelForDatasets = async (model: any) => {
  const dataSetId: string = model['dataSet'];
  if (!dataSetId) return;
  var dataSetItemField: string = '';

  const selectItemQuestion: any = model
    .getAllQuestions()
    .find((value: Survey.SurveyQuestion) => !!value['dataSetItemSelector']);
  if (selectItemQuestion) {
    const name = selectItemQuestion.name;
    dataSetItemField = selectItemQuestion['dataSetItemField'];

    var trigger = new Survey.SurveyTriggerRunExpression();
    model.triggers.push(trigger);
    trigger.name = `${name}-updateFieldFromDataSet`;
    trigger.expression = `{${name}} notempty or {${name}} empty`;
    trigger.runExpression = `updateFieldFromDataSet({${name}})`;
  }
  //model.currentPage = 1;

  const { data: file }: { data: GetCSVFileType | null } = await client.query({
    query: GET_CSVFILE,
    variables: {
      input: {
        workspaceDatasetId: dataSetId,
      },
    },
  });

  if (!file) {
    return;
  }

  Survey.FunctionFactory.Instance.register('updateFieldFromDataSet', (params: any[]) => {
    updateFieldFromDataSet(model, file, params);
  });

  const { data: fileRowData }: { data: GetCSVFileRowSelectColumnsType } = await client.query({
    query: GET_CSVFILE_ROWS_SELECTCOLUMNS,
    variables: {
      input: {
        csvFileId: file.file.id,
        columnsByName: [dataSetItemField],
      },
    },
  });

  if (!fileRowData || fileRowData.rows.length === 0) {
    return;
  }

  if (!selectItemQuestion) {
    return;
  }

  const choices: {
    value: any;
    text: string;
  }[] = [];
  if (!!selectItemQuestion['dataSetItemCanCreate']) {
    if (selectItemQuestion.choices.length > 0) {
      var hasNew = false;
      selectItemQuestion.choices.forEach((choice: any) => {
        choices.push({
          value: choice.value,
          text: choice.text,
        });
        if (choice.value.toLowerCase() === 'new') hasNew = true;
      });
      if (!hasNew) {
        choices.push({
          value: 'NEW',
          text: ' ADD NEW...',
        });
      }
    }
  }
  choices.push(
    ...fileRowData.rows.map(row => {
      return {
        value: row.id,
        text: row.values.length ? row.values[0] : row.id,
      };
    })
  );
  selectItemQuestion.choices = choices;
};

const onBeforeSendRequest = function(sender: any, options: any) {
  const cookieKey = `${JWT_COOKIE_KEY}=`;
  const cookie = document.cookie.split('; ').find(row => row.startsWith(cookieKey));
  if (cookie) {
    const cookieValue = cookie.split('=')[1];
    options.request.setRequestHeader('Authorization', `Bearer ${cookieValue}`);
  }
};

const updateFieldFromDataSet = (model: Survey.Model, csvFile: GetCSVFileType, params: any[]) => {
  if (params.length === 0) return;
  const itemId: string = params[0];
  if (!!itemId && itemId.toLowerCase() !== 'new') {
    client
      .query({
        query: GET_CSVFILE_ROW_ALLCOLUMNS,
        variables: {
          input: {
            csvFileId: csvFile.file.id,
            rowId: itemId,
          },
        },
      })
      .then(result => {
        if (result && result.data && result.data.row) {
          const data: GetCSVFileRowAllColumnsType = result.data;
          if (data.row.updatedAt) {
            model.setValue('datasetSelectedRowLastUpdate', data.row.updatedAt);
          }
          const questions = model
            .getAllQuestions()
            .filter(value => !!value['dataSetItemField'] && !value['dataSetItemSelector']);
          questions.forEach(question => {
            const fieldName: string = question['dataSetItemField'];
            const column = csvFile.file.columns.find(c => c.name.toLowerCase() === fieldName.toLowerCase());
            const columnIndex = column ? column.index : 0;
            model.setValue(question.name, getStringOrArrayValue(data.row.values[columnIndex]));
          });
        }
      });
  } else {
    const questions = model
      .getAllQuestions()
      .filter(value => !!value['dataSetItemField'] && !value['dataSetItemSelector']);
    questions.forEach(question => model.setValue(question.name, question.defaultValue));
  }
};

const isDataSetItemDelete = (model: Survey.SurveyModel): boolean => {
  const dataSetActionDeleteElementName: string | undefined = model['dataSetActionDeleteElement'];
  const answers = model.data;
  var deleteValue: any = dataSetActionDeleteElementName ? answers[dataSetActionDeleteElementName] : false;
  if (typeof deleteValue === 'object' && Array.isArray(deleteValue) && deleteValue.length > 0) {
    deleteValue = deleteValue[0];
  }
  if (typeof deleteValue === 'string') {
    deleteValue = deleteValue.toLowerCase();
    return deleteValue === 'yes' || deleteValue === 'delete';
  } else if (typeof deleteValue === 'boolean') {
    return deleteValue;
  }
  return false;
};

export const getDataSetsToSaveFromModel = (model: Survey.SurveyModel): SaveSurveyDataSetType[] => {
  const dataSets: SaveSurveyDataSetType[] = [];
  if (!!model['dataSet']) {
    const dataSetId: string = model['dataSet'];
    const questions = model.getAllQuestions().filter(q => !!q['dataSetItemField']);
    const item = questions.find(q => !!q['dataSetItemSelector']);
    if (item) {
      const deleteItem = isDataSetItemDelete(model);
      const itemId: string = item.value;
      const dataSet: SaveSurveyDataSetType = {
        dataSetId,
        itemId,
        delete: deleteItem,
        values: [],
        updatedAt: model.getValue('datasetSelectedRowLastUpdate'),
      };
      if (!deleteItem) {
        const answers = model.data;
        questions.forEach(q => {
          if (!q['dataSetItemSelector']) {
            dataSet.values.push({
              fieldName: q['dataSetItemField'] as string,
              value: Array.isArray(answers[q.name])
                ? `[${answers[q.name]
                    .map((item: any) => (typeof item === 'object' ? JSON.stringify(item) : `'${item}'`))
                    .join(',')}]`
                : answers[q.name] && typeof answers[q.name] === 'object'
                ? JSON.stringify(answers[q.name])
                : answers[q.name] !== undefined && answers[q.name] !== null
                ? `${answers[q.name]}`
                : answers[q.name],
            });
          }
        });
      }
      dataSets.push(dataSet);
    }
  }
  return dataSets;
};

const fetchDatasets = async (workspaceId: string): Promise<{ value: string; text: string }[]> => {
  const result = await client.query({
    query: GET_WORKSPACE_DATASETS,
    variables: { workspaceId },
  });
  if (result.data) {
    const data = result.data as GetWorkspaceDatasetsType;
    if (data.datasets) {
      return data.datasets.map(ds => ({
        value: ds.id,
        text: ds.description,
      }));
    }
  }
  return [];
};

const fetchDatasetFields = async (workspaceDatasetId: string): Promise<{ value: string; text: string }[]> => {
  const result = await client.query({
    query: GET_CSVFILE,
    variables: { input: { workspaceDatasetId: workspaceDatasetId } },
  });
  if (result.data) {
    const data = result.data as GetCSVFileType;
    if (data && data.file) {
      return data.file.columns.map(column => ({
        value: column.name,
        text: column.name,
      }));
    }
  }
  return [];
};
